import type {
  CheckInSession,
  CheckInTicketGrouping,
  CreateWallPlaylistRequest,
  PinWallAdRequest,
  ReorderWallAdsRequest,
  SetWallPlaylistRequest,
  UpdateWallAdRequest,
  UpdateWallPlaylistRequest,
  WallAd,
  WallDisplay,
  WallDisplayStatus,
  WallPlaylist,
} from '@techgitter/contracts';

/**
 * Staff client for the reception wall TV (`/api/wall/*` in sm4rt-api).
 *
 * These are the **admin** endpoints, SWA-header authenticated — distinct from
 * the two public endpoints the wall display itself calls
 * (`/api/public/wall*`), which authenticate with the display's own device key.
 * Nothing here ever handles that key: it is issued once to the display and
 * never leaves it, which is why approving a screen is done by matching its
 * pairing code rather than by entering anything.
 */

const API_BASE_URL = '/api';

/** Surface the server's own message — these are operator-facing screens. */
async function failure(response: Response, fallback: string): Promise<Error> {
  const body = await response
    .json()
    .catch(() => ({ error: `${fallback}: ${response.status}` }));
  return new Error(body.error || `HTTP ${response.status}`);
}

async function readJson<T>(response: Response, fallback: string): Promise<T> {
  if (!response.ok) throw await failure(response, fallback);
  return response.json();
}

// ---------------------------------------------------------------------------
// Displays
// ---------------------------------------------------------------------------

export async function fetchWallDisplays(
  signal?: AbortSignal,
  options?: { status?: WallDisplayStatus },
): Promise<WallDisplay[]> {
  const query = options?.status ? `?status=${encodeURIComponent(options.status)}` : '';
  const response = await fetch(`${API_BASE_URL}/wall/displays${query}`, {
    headers: { Accept: 'application/json' },
    signal,
  });
  const data = await readJson<{ items?: WallDisplay[] }>(
    response,
    'Failed to load wall displays',
  );
  return data.items ?? [];
}

/**
 * Approve a display. The server pins the IP **the display** last polled from,
 * not the approver's — so this is also the fix after the shop's public IP
 * changes: re-approving repins to wherever the screen is now.
 */
export async function approveWallDisplay(
  displayId: string,
  signal?: AbortSignal,
): Promise<WallDisplay> {
  const response = await fetch(
    `${API_BASE_URL}/wall/displays/${displayId}/approve`,
    { method: 'POST', signal },
  );
  return readJson<WallDisplay>(response, 'Failed to approve display');
}

/** Revoke a display. It drops to its holding page within one poll (~2s). */
export async function revokeWallDisplay(
  displayId: string,
  signal?: AbortSignal,
): Promise<WallDisplay> {
  const response = await fetch(
    `${API_BASE_URL}/wall/displays/${displayId}/revoke`,
    { method: 'POST', signal },
  );
  return readJson<WallDisplay>(response, 'Failed to revoke display');
}

export async function renameWallDisplay(
  displayId: string,
  name: string,
  signal?: AbortSignal,
): Promise<WallDisplay> {
  const response = await fetch(`${API_BASE_URL}/wall/displays/${displayId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name }),
    signal,
  });
  return readJson<WallDisplay>(response, 'Failed to rename display');
}

/**
 * Hard-delete an enrolment. Prefer {@link revokeWallDisplay} for a screen that
 * was ever live — that keeps the row auditable and stops the same device key
 * ever being approved again.
 */
export async function deleteWallDisplay(
  displayId: string,
  signal?: AbortSignal,
): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/wall/displays/${displayId}`, {
    method: 'DELETE',
    signal,
  });
  if (!response.ok) throw await failure(response, 'Failed to delete display');
}

// ---------------------------------------------------------------------------
// Ads
// ---------------------------------------------------------------------------

export async function fetchWallAds(
  signal?: AbortSignal,
  options?: { includeInactive?: boolean; playlistId?: string },
): Promise<WallAd[]> {
  const params = new URLSearchParams();
  if (options?.includeInactive) params.set('includeInactive', 'true');
  if (options?.playlistId) params.set('playlistId', options.playlistId);
  const query = params.toString() ? `?${params.toString()}` : '';
  const response = await fetch(`${API_BASE_URL}/wall/ads${query}`, {
    headers: { Accept: 'application/json' },
    signal,
  });
  const data = await readJson<{ items?: WallAd[] }>(
    response,
    'Failed to load wall ads',
  );
  return data.items ?? [];
}

/**
 * Upload an ad image.
 *
 * The body is the **raw bytes** — matching the check-in photo upload rather
 * than introducing a multipart parser for one route — with the label and dwell
 * as **query parameters**.
 *
 * The label must not travel in a header. HTTP header values are limited to
 * ISO-8859-1, and `fetch` throws outright ("String contains non ISO-8859-1
 * code point") before sending if the string holds anything outside it. Ad
 * labels default to the filename, and real filenames routinely contain a
 * curly apostrophe, an en dash, an accented letter or an emoji — so a header
 * makes ordinary uploads fail. `URLSearchParams` percent-encodes anything.
 */
export async function uploadWallAd(
  file: File,
  options?: { name?: string; dwellMs?: number; playlistId?: string },
  signal?: AbortSignal,
): Promise<WallAd> {
  // Some pickers (network drives, certain drag sources) hand over a File with
  // no type. Catch it here with a message that says what to do, rather than
  // letting it read as "unsupported format" from the server.
  if (!file.type) {
    throw new Error(
      `Could not tell what kind of image “${file.name}” is. Re-save it as a JPG, PNG or WebP and try again.`,
    );
  }

  const params = new URLSearchParams({ name: options?.name?.trim() || file.name });
  if (options?.dwellMs != null) params.set('dwellMs', String(options.dwellMs));
  if (options?.playlistId) params.set('playlistId', options.playlistId);

  const response = await fetch(`${API_BASE_URL}/wall/ads?${params.toString()}`, {
    method: 'POST',
    headers: { 'Content-Type': file.type },
    body: file,
    signal,
  });
  return readJson<WallAd>(response, 'Failed to upload image');
}

export async function updateWallAd(
  adId: string,
  payload: UpdateWallAdRequest,
  signal?: AbortSignal,
): Promise<WallAd> {
  const response = await fetch(`${API_BASE_URL}/wall/ads/${adId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
    signal,
  });
  return readJson<WallAd>(response, 'Failed to update image');
}

/** Soft delete — the row and its blob are kept for audit. */
export async function removeWallAd(
  adId: string,
  signal?: AbortSignal,
): Promise<WallAd> {
  const response = await fetch(`${API_BASE_URL}/wall/ads/${adId}`, {
    method: 'DELETE',
    signal,
  });
  return readJson<WallAd>(response, 'Failed to remove image');
}

/**
 * Delete an ad for good — the Dataverse row **and** its image file in Blob
 * Storage. Not undoable, and deliberately a different route from
 * {@link removeWallAd} rather than a flag on it: a query parameter would put
 * the irreversible action one typo away from the reversible one.
 */
export async function purgeWallAd(
  adId: string,
  signal?: AbortSignal,
): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/wall/ads/${adId}/purge`, {
    method: 'DELETE',
    signal,
  });
  if (!response.ok) throw await failure(response, 'Failed to delete image');
}

export async function reorderWallAds(
  orderedIds: string[],
  signal?: AbortSignal,
): Promise<WallAd[]> {
  const payload: ReorderWallAdsRequest = { orderedIds };
  const response = await fetch(`${API_BASE_URL}/wall/ads/reorder`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
    signal,
  });
  const data = await readJson<{ items?: WallAd[] }>(
    response,
    'Failed to reorder images',
  );
  return data.items ?? [];
}

// ---------------------------------------------------------------------------
// Playlists
// ---------------------------------------------------------------------------

export async function fetchWallPlaylists(
  signal?: AbortSignal,
  options?: { includeInactive?: boolean },
): Promise<WallPlaylist[]> {
  const query = options?.includeInactive ? '?includeInactive=true' : '';
  const response = await fetch(`${API_BASE_URL}/wall/playlists${query}`, {
    headers: { Accept: 'application/json' },
    signal,
  });
  const data = await readJson<{ items?: WallPlaylist[] }>(
    response,
    'Failed to load playlists',
  );
  return data.items ?? [];
}

/**
 * Create a rotation. `role` wires it into automatic selection — at most one
 * active playlist may hold each of `default` / `residential` / `business`, and
 * setting it here releases it from whichever playlist held it before.
 */
export async function createWallPlaylist(
  payload: CreateWallPlaylistRequest,
  signal?: AbortSignal,
): Promise<WallPlaylist> {
  const response = await fetch(`${API_BASE_URL}/wall/playlists`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
    signal,
  });
  return readJson<WallPlaylist>(response, 'Failed to create playlist');
}

export async function updateWallPlaylist(
  playlistId: string,
  payload: UpdateWallPlaylistRequest,
  signal?: AbortSignal,
): Promise<WallPlaylist> {
  const response = await fetch(`${API_BASE_URL}/wall/playlists/${playlistId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
    signal,
  });
  return readJson<WallPlaylist>(response, 'Failed to update playlist');
}

/** Retire a rotation. Its ads keep pointing at it, so reinstating restores it. */
export async function removeWallPlaylist(
  playlistId: string,
  signal?: AbortSignal,
): Promise<WallPlaylist> {
  const response = await fetch(`${API_BASE_URL}/wall/playlists/${playlistId}`, {
    method: 'DELETE',
    signal,
  });
  return readJson<WallPlaylist>(response, 'Failed to retire playlist');
}

/**
 * The employee override — switch the wall for the customer at the counter.
 *
 * Contextual, not a setting: pass `sessionId` and it ends when that check-in
 * completes; otherwise only the timeout (default 20 min) ends it.
 */
export async function setWallPlaylist(
  payload: SetWallPlaylistRequest,
  signal?: AbortSignal,
): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/wall/playlist`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
    signal,
  });
  if (!response.ok) throw await failure(response, 'Failed to switch playlist');
}

/** Drop the override — back to customer-driven / default selection. */
export async function clearWallPlaylist(signal?: AbortSignal): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/wall/playlist`, {
    method: 'DELETE',
    signal,
  });
  if (!response.ok) throw await failure(response, 'Failed to clear the switch');
}

// ---------------------------------------------------------------------------
// Pin
// ---------------------------------------------------------------------------

/**
 * Put an image on the wall now. It comes down on whichever happens first: the
 * next check-in being started, or `durationSeconds` (default one hour).
 */
export async function pinWallAd(
  adId: string,
  durationSeconds?: number,
  signal?: AbortSignal,
): Promise<void> {
  const payload: PinWallAdRequest = { adId };
  if (durationSeconds != null) payload.durationSeconds = durationSeconds;
  const response = await fetch(`${API_BASE_URL}/wall/pin`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
    signal,
  });
  if (!response.ok) throw await failure(response, 'Failed to pin image');
}

export async function clearWallPin(signal?: AbortSignal): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/wall/pin`, {
    method: 'DELETE',
    signal,
  });
  if (!response.ok) throw await failure(response, 'Failed to clear the pin');
}

// ---------------------------------------------------------------------------
// Check-in trigger
// ---------------------------------------------------------------------------

/**
 * Start a check-in and put its QR on the wall. Returns the new session so the
 * caller can go straight into its staff console while the customer scans.
 */
/**
 * Put an EXISTING check-in's QR on the reception wall.
 *
 * The counterpart to {@link startWallCheckIn}: that creates a blank draft and
 * shows it, this shows a check-in reception already built on the form. Returns
 * the updated session so the caller can refresh in place.
 */
export async function sendCheckInToWall(
  sessionId: string,
  signal?: AbortSignal,
): Promise<CheckInSession> {
  const response = await fetch(
    `${API_BASE_URL}/wall/check-in/${sessionId}/send`,
    { method: 'POST', signal },
  );
  return readJson<CheckInSession>(
    response,
    'Failed to send the QR code to the wall',
  );
}

export async function startWallCheckIn(
  options?: { playlistId?: string; ticketGrouping?: CheckInTicketGrouping },
  signal?: AbortSignal,
): Promise<{ id: string; sessionNumber?: string }> {
  // The playlist rides the same call rather than a second request: choosing a
  // rotation is part of the same decision as starting the check-in, and two
  // requests could half-fail.
  //
  // So does the ticket grouping. The wall creates the session before any
  // device exists, so this is the only moment it can be chosen — and it has no
  // default: a grouping nobody picked is invisible by the time the tickets
  // exist.
  const body: Record<string, unknown> = {};
  if (options?.playlistId) body.playlistId = options.playlistId;
  if (options?.ticketGrouping) body.ticketGrouping = options.ticketGrouping;

  const response = await fetch(`${API_BASE_URL}/wall/check-in/start`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    signal,
  });
  return readJson(response, 'Failed to start the wall check-in');
}
